import { z } from '@customerio/hydra-validation';
import type { MailServer as UIAPIMailServer, MailserversRequestBody } from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Mail-server entity — the custom SMTP servers a workspace sends through.
 *
 * Wire shape uses unix-second `created_at` / `updated_at`. They stay numbers:
 * the SMTP settings screens render address, routing and pinned domains, never
 * the timestamps, so Date transforms would be churn for no consumer.
 */
export type MailServer = UIAPIMailServer;

/**
 * `id` and `default_for` are asserted at parse time: the id keys the cache and
 * `default_for` drives which server the routing column marks as default, so a
 * malformed response should surface as a Zod error rather than silently
 * mis-render the table. Other fields pass through via `looseObject`.
 */
const MailServerFromApi = readSchema<MailServer>(
  z.looseObject({
    id: z.string(),
    default_for: z.string(),
  }),
);

/**
 * The create/update body fields, derived from the generated request so the
 * write surface can't drift from the spec. Both operations take the same
 * `{ mail_server: … }` envelope, which the mutations add.
 */
export type MailServerWriteInput = MailserversRequestBody['mail_server'];

/** @public */
export const mailServer = defineEntity<MailServer, MailServerWriteInput, void, undefined, 'workspace'>({
  name: 'mail-server',
  scope: 'workspace',
  pk: (server) => server.id,
  schema: { read: MailServerFromApi },
});

export const mailServerKeys = mailServer.keys;

export function parseMailServer(raw: UIAPIMailServer): MailServer {
  return mailServer.parse(raw);
}
